import { V2ProductionCostGetGroupEnergyUnitListPostResponse } from '@maxtropy/device-customer-apis-v2';
import { DataNode } from 'antd/es/tree';
import electricityActive from './imgs/electricityActive.png';
import electricityPassive from './imgs/electricityPassive.png';
import waterActive from './imgs/waterActive.png';
import waterPassive from './imgs/waterPassive.png';
import gasActive from './imgs/gasActive.png';
import gasPassive from './imgs/gasPassive.png';
import hotSteamActive from './imgs/hotSteamActive.png';
import hotSteamPassive from './imgs/hotSteamPassive.png';
import compressedAirActive from './imgs/compressedAirActive.png';
import compressedAirPassive from './imgs/compressedAirPassive.png';
import IndustrialGasActive from './imgs/IndustrialGasActive.png';
import IndustrialGasPassive from './imgs/IndustrialGasPassive.png';
import haActive from './imgs/haActive.png';
import haPassive from './imgs/haPassive.png';
import IndustrialLiquidActive from './imgs/IndustrialLiquidActive.png';
import IndustrialLiquidPassive from './imgs/IndustrialLiquidPassive.png';
import { isNil } from 'lodash-es';
import dayjs, { Dayjs } from 'dayjs';
import { TimeOfUseTariffTypeColor } from '@/shared/types';
import { AnalysisItem } from './components/EnergyTree';

// 生成节点映射表（树形结构-->平铺）
export const generateList = (data: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']) => {
  let tempDataList: { key: React.Key; title: string }[] = [];
  const loopData = (data: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']) => {
    for (let i = 0; i < (data ?? []).length; i++) {
      const node = data?.[i];
      tempDataList.push({ key: node?.energyUnitId ?? '', title: node?.name ?? '' });
      if (node?.children) {
        loopData(node.children);
      }
    }
  };
  loopData(data);
  return tempDataList;
};

// 初始化展开的节点列表
export const initExpandedKeys = (data: AnalysisItem[]) => {
  let keys: number[] = [];
  data.forEach(item => {
    if (!item.disabled && item.energyUnitId) {
      keys.push(item.energyUnitId!);
    }
  });
  return keys;
};

// 工具函数：递归收集所有子节点 key
export const getAllChildKeys = (node: any): string[] => {
  let keys: string[] = [];
  keys.push(node.key);
  if (node.children) {
    node.children.forEach((child: any) => {
      keys.push(child.key);
      keys = keys.concat(getAllChildKeys(child));
    });
  }
  return keys;
};

// 获取有权限的用能单元
export const initExpandedKeysWithPermission = (data: DataNode[]) => {
  let keys: number[] = [];
  const loop = (nodeList: DataNode[]) => {
    for (const item of nodeList ?? []) {
      if (!item.disabled) {
        keys.push(item.key as number);
      }
      if (item.children) {
        loop(item.children);
      }
    }
  };
  loop(data);
  return keys;
};

// 获取父节点key
export const getParentKey = (
  key: React.Key,
  tree: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']
): React.Key => {
  let parentKey: React.Key;
  for (let i = 0; i < (tree ?? []).length; i++) {
    const node = tree?.[i];
    if (node?.children) {
      if (node.children.some(item => item.energyUnitId === key)) {
        parentKey = node?.energyUnitId ?? '';
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }
  return parentKey!;
};

// 递归查找某个节点下所有子节点 key
export const getAllDescendantKeys = (node: DataNode): string[] => {
  let keys = [node.key as string];
  if (node.children) {
    node.children.forEach(child => {
      keys = keys.concat(getAllDescendantKeys(child));
    });
  }
  return keys;
};

// 根据 key 在 treeData 中查找对应 node
export const findNodeByKey = (key: React.Key, nodes: DataNode[]): DataNode | null => {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(key, node.children);
      if (found) return found;
    }
  }
  return null;
};

// 全选或级联
export enum AllorCascader {
  // 全选
  ALL = 1,
  // 级联
  CASCADER = 2,
}

export const AllorCascaderDisplay = {
  [AllorCascader.ALL]: '全选',
};

// 时间颗粒度
export enum DateType {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
  CUSTOM = 'CUSTOM',
}

export const DateTypeDisplay = {
  [DateType.DAY]: '日',
  [DateType.WEEK]: '周',
  [DateType.MONTH]: '月',
  [DateType.YEAR]: '年',
  [DateType.CUSTOM]: '自定义',
};

// 工质场景 id 枚举
export enum EnergyMediumSceneIds {
  WATER_SUPPLY_AND_DRAINAGE = 500, // 给排水/用气
  GAS_SUPPLY_AND_CONSUMPTION = 501, // 供气/用气
  HOT_STEAM = 502, // 热蒸汽
  COMPRESSED_AIR = 503, // 压缩空气
  INDUSTRIAL_GASES = 504, // 工业气体
  HEATING_VENTILATION_AND_AIR_CONDITIONING = 505, // 供暖通风与空气调节
  INDUSTRIAL_LIQUIDS = 506, // 工业液体
  ELEC = 507, // 电能
}

// 工质 icon 映射
export const EnergyMediumIconDisplay = {
  [EnergyMediumSceneIds.WATER_SUPPLY_AND_DRAINAGE]: {
    active: waterActive,
    passive: waterPassive,
  },
  [EnergyMediumSceneIds.GAS_SUPPLY_AND_CONSUMPTION]: {
    active: gasActive,
    passive: gasPassive,
  },
  [EnergyMediumSceneIds.HOT_STEAM]: {
    active: hotSteamActive,
    passive: hotSteamPassive,
  },
  [EnergyMediumSceneIds.COMPRESSED_AIR]: {
    active: compressedAirActive,
    passive: compressedAirPassive,
  },
  [EnergyMediumSceneIds.INDUSTRIAL_GASES]: {
    active: IndustrialGasActive,
    passive: IndustrialGasPassive,
  },
  [EnergyMediumSceneIds.HEATING_VENTILATION_AND_AIR_CONDITIONING]: {
    active: haActive,
    passive: haPassive,
  },
  [EnergyMediumSceneIds.INDUSTRIAL_LIQUIDS]: {
    active: IndustrialLiquidActive,
    passive: IndustrialLiquidPassive,
  },
  [EnergyMediumSceneIds.ELEC]: {
    active: electricityActive,
    passive: electricityPassive,
  },
};

export enum RankMethod {
  // 按用量
  BY_USAGE = 1,
  // 按增量
  BY_INCREMENT,
}

export const RankMethodDisplay = {
  [RankMethod.BY_USAGE]: '用量',
  [RankMethod.BY_INCREMENT]: '增量',
};

// 尖峰平谷总
export enum RankMethodType {
  ALL = 'TOTAL',
  SHARP = 'SUMMIT', // 尖
  PEAK = 'PEAK', // 峰
  AVERAGE = 'PLAIN', // 平
  VALLEY = 'VALLEY', // 谷
}

export const RankMethodTypeDisplay = {
  [RankMethodType.ALL]: '总',
  [RankMethodType.SHARP]: '尖时段',
  [RankMethodType.PEAK]: '峰时段',
  [RankMethodType.AVERAGE]: '平时段',
  [RankMethodType.VALLEY]: '谷时段',
};

// 数字转中文
export enum NumToChinese {
  ONE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  FIVE = 5,
  SIX = 6,
  SEVEN = 7,
  EIGHT = 8,
  NINE = 9,
  TEN = 10,
}

export const NumToChineseDisplay = {
  [NumToChinese.ONE]: '一级用能单元',
  [NumToChinese.TWO]: '二级用能单元',
  [NumToChinese.THREE]: '三级用能单元',
  [NumToChinese.FOUR]: '四级用能单元',
  [NumToChinese.FIVE]: '五级用能单元',
  [NumToChinese.SIX]: '六级用能单元',
  [NumToChinese.SEVEN]: '七级用能单元',
  [NumToChinese.EIGHT]: '八级用能单元',
  [NumToChinese.NINE]: '九级用能单元',
  [NumToChinese.TEN]: '十级用能单元',
};

/**
 * 获取默认时间
 */
export const getDefaultTime = (mode: DateType, date: Dayjs | null) => {
  if (mode === DateType.CUSTOM) return {};
  switch (mode) {
    case DateType.DAY:
      return {
        startTime: dayjs(date).startOf('day').valueOf(),
        endTime: dayjs(date).endOf('day').valueOf(),
      };
    case DateType.WEEK:
      return {
        startTime: dayjs(date).startOf('week').valueOf(),
        endTime: dayjs(date).endOf('week').valueOf(),
      };
    case DateType.MONTH:
      return {
        startTime: dayjs(date).startOf('month').valueOf(),
        endTime: dayjs(date).endOf('month').valueOf(),
      };
    case DateType.YEAR:
      return {
        startTime: dayjs(date).startOf('year').valueOf(),
        endTime: dayjs(date).endOf('year').valueOf(),
      };
    default:
      return {
        startTime: dayjs(date).startOf('day').valueOf(),
        endTime: dayjs(date).endOf('day').valueOf(),
      };
  }
};

enum EPricePeakType {
  // 尖
  SUMMIT = 1,
  // 峰
  PEAK,
  // 平
  PLAIN,
  // 谷
  VALLEY,
}

export interface EPriceTime {
  type: EPricePeakType;
  intervalStart: number;
  intervalEnd: number;
}
export interface AllEPriceTime {
  [key: number]: EPriceTime[];
}

//获取两个时间之前的所有日期
export const getAllDates = (startTime: number, endTime: number) => {
  const diff = dayjs(endTime).diff(dayjs(startTime), 'd');
  let dates = [startTime];
  for (let i = 1; i <= diff; i++) {
    dates.push(dayjs(startTime).add(i, 'd').valueOf());
  }
  return dates;
};

// 获取EPriceTimes（后端直接返回key为日期的时间戳）
export const getEPriceTimesByDay = (dates: number[], allEprices: AllEPriceTime) => {
  let times: EPriceTime[] = [];
  if (Object.keys(allEprices).length !== 0) {
    dates.forEach((d, i) => {
      const value = allEprices[dayjs(d).valueOf()];
      value?.forEach(f => {
        times.push({
          type: f.type,
          intervalStart: f.intervalStart + i * 48,
          intervalEnd: f.intervalEnd + i * 48,
        });
      });
    });
  }
  return times;
};

export const getMarkAreaData = (ePriceTimes?: EPriceTime[], query?: any) => {
  return ePriceTimes?.map(item => {
    const { intervalStart, intervalEnd, type } = item;
    const color = `${TimeOfUseTariffTypeColor[type]}40`;
    const interval = (30 * 60 * 1000) / (15 * 60 * 1000);
    const start = intervalStart * interval;
    const end = (intervalEnd + 1) * interval;
    // 开始时间与结束时间的时间戳，以前是用类目轴是start与end
    // 减去0.5，使两个时间段中间的柱子在markArea之内，而不是横跨两个markArea。
    const startTime = query.startTime + 15 * 60 * 1000 * (start - 0.5);
    const endTime = query.startTime + 15 * 60 * 1000 * (end - 0.5);
    return [
      {
        xAxis: startTime,
        itemStyle: {
          color,
        },
      },
      {
        xAxis: endTime,
      },
    ];
  });
};

export const formatValueAndSign = (value: number, increment?: number, type?: RankMethod) => {
  if (type === RankMethod.BY_USAGE) {
    return {
      value: isNil(value) ? 0 : value,
    };
  }
  if (isNil(increment) || increment === 0) {
    return {
      value: 0,
      sign: undefined,
    };
  }

  if (increment > 0) {
    return {
      value: increment,
      sign: '+',
    };
  } else {
    return {
      value: Math.abs(increment),
      sign: '-',
    };
  }
};

export const xLabel = (timeResolution?: DateType, ts?: any) => {
  if (timeResolution === DateType.MONTH) {
    return 'YYYY-MM';
  } else if (timeResolution === DateType.WEEK) {
    const year = dayjs(ts).year();
    const week = dayjs(ts).week();
    return `${year}-${week}周`;
  } else if (timeResolution === DateType.YEAR) {
    return 'YYYY';
  } else {
    return 'YYYY-MM-DD';
  }
};
